import { FormComponent } from './form.component';
import { SelectItem } from '@openng/optimus-ui/api';
import { ContactApiService } from 'src/app/services/api/contact-api.service';
import { LocationApiService } from 'src/app/services/api/location-api.service';
import { FacilityApiService } from 'src/app/services/api/facility-api.service';
import { FacilityTableService } from 'src/app/services/table/facility-table.service';
import { take } from 'rxjs/operators';
import { FilterCriteria } from 'src/app/model/common/filterCriteria';
import {
  dynamicSort,
  filterDropdownResults,
} from '../functions/common-functions';
import { AbstractControl, UntypedFormGroup } from '@angular/forms';
import { TabService } from '../services/tab.service';
import { forkJoin, Subscription } from 'rxjs';
import {
  Input,
  Component,
  input,
  output,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import { isNullOrUndefined } from 'src/app/utils/utils';
import {
  AutoCompleteCompleteEvent,
  AutoCompleteSelectEvent,
} from '@openng/optimus-ui/autocomplete';
import { AorApiService } from 'src/app/services/api/aor-api.service';
import { AdminApiService } from 'src/app/services/api/admin-api.service';

export type DetailsChangesEvent = {
  isDirty: boolean;
  index: number;
  id: string;
};

@Component({
  template: '',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class DetailsContainer extends FormComponent {
  protected _locationApi = inject(LocationApiService);
  protected _contactApi = inject(ContactApiService);
  protected _facilityTableService = inject(FacilityTableService);
  protected _facilityApi = inject(FacilityApiService);
  protected _aorApi = inject(AorApiService);
  protected _tabService = inject(TabService);
  protected _adminApi = inject(AdminApiService);

  private lId!: string;
  @Input() set id(id) {
    this.lId = id;
  }

  get id() {
    return this.lId;
  }

  readonly index = input<number>(-1);

  readonly changesEvent = output<DetailsChangesEvent>();

  states: string[] = [];
  filteredStates: string[] = [];
  counties: any[] = [];
  filteredCounties: any[] = [];
  townships: any[] = [];
  filteredTownships: any[] = [];

  availableCompanies: SelectItem[] = [];
  availableAORs: SelectItem[] = [];

  autoSelect = false;

  doCheckComplete = false;
  valueSub?: Subscription;
  tabClosingSub?: Subscription;
  loading = true;

  loadDropDownData(): void {
    this._locationApi.getStates().subscribe((states) => {
      this.states = states;
    });

    this._locationApi.getCounties().subscribe((counties) => {
      this.counties = counties.sort(dynamicSort('county', 1));
    });

    this._locationApi.getTownships().subscribe((townships) => {
      this.townships = townships;
    });

    this._adminApi.getCompanies().subscribe((companies) => {
      this.availableCompanies = [
        { label: 'Choose', value: null },
        ...companies
          .map((c) => ({
            label: c.name,
            value: c.id,
          }))
          .sort(dynamicSort('label', 1)),
      ];
    });

    this._aorApi.getAORs(null).subscribe((aors) => {
      this.availableAORs = [
        { label: 'Choose', value: null },
        ...aors
          .map((d: { name: string; id: string }) => ({
            label: d.name,
            value: d.id,
          }))
          .sort(dynamicSort('label', 1)),
      ];
    });
  }

  stateSearch($event: AutoCompleteCompleteEvent) {
    const queryArray: FilterCriteria[] = [];
    if ($event.query !== '') {
      queryArray.push(new FilterCriteria('state', $event.query, 'startsWith'));
    }

    this.filteredStates = this.states
      .filter((state) =>
        state.toLowerCase().startsWith($event.query.toLowerCase())
      )
      .sort();
  }

  selectState(
    $event: AutoCompleteSelectEvent,
    customForm: UntypedFormGroup | AbstractControl = this.form!
  ) {
    const fg = customForm as UntypedFormGroup;
    fg.patchValue({
      county: null,
      township: null,
    });
  }

  countySearch(
    $event: AutoCompleteCompleteEvent,
    customForm: UntypedFormGroup | AbstractControl = this.form!
  ) {
    const fg = customForm as UntypedFormGroup;
    const state = fg.controls['state'].value;

    const queryArray: FilterCriteria[] = [];
    if (state) {
      queryArray.push(new FilterCriteria('state', state, 'include'));
    }
    if ($event.query !== '') {
      queryArray.push(new FilterCriteria('county', $event.query, 'include'));
    }

    let counties = filterDropdownResults(this.counties, queryArray);
    counties.sort(dynamicSort('county', 1));
    this.filteredCounties = counties;
    if (this.autoSelect && this.filteredCounties.length === 1) {
      customForm.patchValue({ county: this.filteredCounties[0] });
      this.selectCounty(
        {
          value: this.filteredCounties[0],
          originalEvent: null as unknown as Event,
        },
        customForm
      );
    }
  }

  selectCounty(
    $event: AutoCompleteSelectEvent,
    customForm: UntypedFormGroup | AbstractControl = this.form!
  ) {
    const eventValue = $event.value;
    const fg = customForm as UntypedFormGroup;
    fg.patchValue({
      state: eventValue.state,
      township: null,
    });

    fg.controls['state'].markAsDirty();
  }

  townshipSearch(
    $event: AutoCompleteCompleteEvent,
    customForm: UntypedFormGroup | AbstractControl = this.form!
  ) {
    const fg = customForm as UntypedFormGroup;
    const state = fg.controls['state'] ? fg.controls['state'].value : null;
    const county =
      fg.controls['county'] && fg.controls['county'].value
        ? fg.controls['county'].value.county
        : null;

    const queryArray: FilterCriteria[] = [];
    if (state) {
      queryArray.push(new FilterCriteria('state', state, 'include'));
    }
    if (county) {
      queryArray.push(new FilterCriteria('county', county, 'include'));
    }
    if ($event.query !== '') {
      queryArray.push(new FilterCriteria('township', $event.query, 'include'));
    }

    let townships = filterDropdownResults(this.townships, queryArray);
    townships.sort(dynamicSort('township', 1));
    this.filteredTownships = townships;
    if (this.autoSelect && this.filteredTownships.length === 1) {
      customForm.patchValue({ township: this.filteredTownships[0] });
      this.selectTownship(
        {
          value: this.filteredTownships[0],
          originalEvent: null as unknown as Event,
        },
        customForm
      );
    }
  }

  selectTownship(
    $event: AutoCompleteSelectEvent,
    customForm: UntypedFormGroup | AbstractControl = this.form!
  ) {
    const eventValue = $event.value;
    const fg = customForm as UntypedFormGroup;
    fg.patchValue({
      locationId: eventValue.id,
      state: eventValue.state,
      county: { county: eventValue.county, state: eventValue.state },
    });

    fg.controls['county'].markAsDirty();
    fg.controls['state'].markAsDirty();
  }

  showActionsDropdown(object: any): boolean {
    return (
      !isNullOrUndefined(object) &&
      !isNullOrUndefined(object.id) &&
      !this._deviceService.isMobile()
    );
  }

  setupTabClosingSubscription(): void {
    this.tabClosingSub = this._tabService.attemptCloseEvent.subscribe(
      (closingTab) => {
        if (!isNullOrUndefined(this.id)) {
          if (this.id === closingTab.id && (!this.form || !this.form.dirty)) {
            this._tabService.closeTab(closingTab.index);
          } else if (
            this.id === closingTab.id &&
            this.form &&
            this.form.dirty
          ) {
            const unsaved$ = this._translateService.get(
              'COMMON.MESSAGES.CONFIRMATION.UNSAVED_CHANGES'
            );
            const yes$ = this._translateService.get(
              'COMMON.LABEL.BUTTONS.LEAVE'
            );
            const no$ = this._translateService.get(
              'COMMON.LABEL.BUTTONS.CANCEL'
            );
            const header$ = this._translateService.get(
              'COMMON.LABEL.HEADERS.LEAVE_PAGE'
            );
            forkJoin(unsaved$, yes$, no$, header$).subscribe((messages) => {
              this._confirmationService.confirm({
                header: messages[3],
                icon: 'fa fa-question-circle',
                acceptVisible: true,
                rejectVisible: true,
                acceptLabel: messages[1],
                rejectLabel: messages[2],
                message: messages[0],
                accept: () => {
                  if (!isNullOrUndefined(closingTab)) {
                    this._tabService.closeTab(closingTab.index);
                  }
                },
                reject: () => {
                  this._tabService.setActiveTab(closingTab.index);
                  this._tabService.revertTabChangeEvent.emit();
                },
              });
            });
          }
        }
      }
    );
  }
}
